import React, { useEffect, useRef, useState } from 'react';
import { PauseIcon, PlayIcon } from 'lucide-react';
import { CountdownRing } from './components/CountdownRing';
import { StepNav } from './components/StepNav';
import { PlaybackProvider } from './hooks/usePlayback';
import { ScreenPromise } from './components/screens/ScreenPromise';
import { ScreenQuestion } from './components/screens/ScreenQuestion';
import { ScreenProblem } from './components/screens/ScreenProblem';
import { ScreenSolution } from './components/screens/ScreenSolution';
import { ScreenReward } from './components/screens/ScreenReward';
import { BRAND, BRAND_LOGO, TOTAL_SECONDS } from './data/teaser';
type Screen = 'promise' | 'question' | 'problem' | 'solution' | 'reward';
const CHAPTERS: Screen[] = ['question', 'problem', 'solution', 'reward'];
const LABELS: Record<Screen, string> = {
  promise: 'The promise',
  question: 'Your take',
  problem: 'The challenge',
  solution: 'The solution',
  reward: 'The reward'
};
const TICK_MS = 100;
export function App() {
  const [screen, setScreen] = useState<Screen>('promise');
  const [remaining, setRemaining] = useState<number>(TOTAL_SECONDS);
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!running || paused) return;
    intervalRef.current = setInterval(() => {
      setRemaining(prev => Math.max(0, prev - TICK_MS / 1000));
    }, TICK_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, paused]);

  // The clock simply stops at zero — it never moves the user on by itself
  useEffect(() => {
    if (running && remaining <= 0) setRunning(false);
  }, [running, remaining]);
  const start = () => {
    setPaused(false);
    setRunning(true);
    setScreen('question');
  };
  const goTo = (next: Screen) => {
    if (next === 'reward') {
      setRunning(false);
      setRemaining(0);
    } else if (next !== 'promise' && remaining > 0) {
      setRunning(true);
    }
    setScreen(next);
  };
  const restart = () => {
    setRunning(false);
    setPaused(false);
    setRemaining(TOTAL_SECONDS);
    setScreen('promise');
  };
  const showChrome = screen !== 'promise';
  const showPauseButton = screen !== 'promise' && screen !== 'reward';
  const chapterIndex = CHAPTERS.indexOf(screen);
  const nav = (() => {
    switch (screen) {
      case 'question':
        return null;
      case 'problem':
        return <StepNav backLabel={LABELS.question} onBack={() => goTo('question')} nextLabel={`See ${LABELS.solution.toLowerCase()}`} onNext={() => goTo('solution')} data-id="element-0" data-mp="3bnmf5h2t937a" />;
      case 'solution':
        return <StepNav backLabel={LABELS.problem} onBack={() => goTo('problem')} nextLabel={`See ${LABELS.reward.toLowerCase()}`} onNext={() => goTo('reward')} data-id="element-1" data-mp="2xudec4sxhq0d" />;
      case 'reward':
        return <StepNav backLabel={LABELS.solution} onBack={() => goTo('solution')} onRestart={restart} data-id="element-2" data-mp="18byasb3eml36" />;
      default:
        return null;
    }
  })();
  const content = <div className="flex h-full w-full flex-col bg-solventum-bg px-6 pb-3 pt-2 selection:bg-solventum-primary selection:text-solventum-bg" data-id="element-3" data-mp="13eevr8yvu4a6">
      {showChrome && <header className="flex w-full flex-shrink-0 items-center justify-between pb-2 pt-3" data-id="element-4" data-mp="1r2698fw5snd1">
          <div className="flex flex-col" data-id="element-5" data-mp="1w2kgvwtulvaj">
            <img src={BRAND_LOGO} alt={BRAND} className="h-7 w-auto object-contain" data-id="element-6" data-mp="1i4417ge0cug9" />
            <span className="mt-1 font-body text-[11px] tracking-wide text-white/45" data-id="element-7" data-mp="1m3l6o9tfprlf">
              {LABELS[screen]}
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-8" data-mp="apr856qnv2tg">
            {showPauseButton && <button onClick={() => setPaused(prev => !prev)} aria-pressed={paused} aria-label={paused ? 'Resume the teaser' : 'Pause the teaser'} className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/80 transition-colors duration-150 hover:border-solventum-primary hover:text-solventum-primary" data-id="element-9" data-mp="qx5hw2u8iwcl">
                {paused ? <PlayIcon className="h-4 w-4" data-id="element-10" data-mp="19v8byw1n3jh9" /> : <PauseIcon className="h-4 w-4" data-id="element-11" data-mp="14xyf4vbx85je" />}
              </button>}
            <CountdownRing remaining={remaining} stopped={screen === 'reward'} paused={paused} data-id="element-12" data-mp="zwsg6fro3m1f" />
          </div>
        </header>}

      <main className="relative min-h-0 flex-1 overflow-hidden" data-id="element-13" data-mp="3kob2ubepzy3p">
        <PlaybackProvider paused={paused} data-id="element-14" data-mp="3gmh80jr7hobg">
          <div key={screen} className="h-full w-full animate-push-in" data-id="element-15" data-mp="3glo8o3uku13c">
            {screen === 'promise' && <ScreenPromise onStart={start} data-id="element-16" data-mp="1w5ghv1xpig9j" />}
            {screen === 'question' && <ScreenQuestion onAnswer={() => goTo('problem')} data-id="element-17" data-mp="2058nb5rt06ao" />}
            {screen === 'problem' && <ScreenProblem data-id="element-18" data-mp="25424p9a7ohg4" />}
            {screen === 'solution' && <ScreenSolution data-id="element-19" data-mp="253abg5436vt0" />}
            {screen === 'reward' && <ScreenReward data-id="element-20" data-mp="29wlhi412r8jv" />}
          </div>
        </PlaybackProvider>
      </main>

      {nav}

      {chapterIndex >= 0 && <div className="flex w-full flex-shrink-0 items-center justify-center gap-1.5 pt-3" aria-hidden="true" data-id="element-21" data-mp="2ex0sioxvw3yo">
          {CHAPTERS.map((chapter, index) => <span key={chapter} className={`h-1.5 rounded-full transition-all duration-300 ${index === chapterIndex ? 'w-5 bg-solventum-primary' : 'w-1.5 bg-white/20'}`} data-id="element-22" data-mp="2nwe3jm1np0li" data-mpi={index} />)}
        </div>}

      <footer className="w-full flex-shrink-0 pt-2 text-center" data-id="element-23" data-mp="yir825wrizd4">
        <p className="font-body text-[11px] tracking-wide text-white/30" data-id="element-24" data-mp="21sdf6457xrh8">
          © Solventum 2026
        </p>
      </footer>
    </div>;
  return <div className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-200 px-4 py-8" data-id="element-25" data-mp="2as8ricaifsiu">
      <div className="relative flex h-[844px] w-[390px] flex-col overflow-hidden rounded-[3rem] border-[10px] border-gray-800 bg-solventum-bg shadow-2xl" data-id="element-26" data-mp="25tfesm31s5jq">
        <div className="absolute inset-x-0 top-0 z-30 flex h-7 justify-center bg-solventum-bg" data-id="element-27" data-mp="2kolopk59koic">
          <div className="h-5 w-28 rounded-b-2xl bg-gray-800" data-id="element-28" data-mp="2fr3c9js1fr5l" />
        </div>
        <div className="absolute inset-x-0 bottom-1.5 z-30 flex justify-center" data-id="element-29" data-mp="2thgj1it70ztc">
          <div className="h-1 w-32 rounded-full bg-white/20" data-id="element-30" data-mp="2pgwlie4pju6o" />
        </div>
        <div className="mt-6 flex-1 overflow-hidden" data-id="element-31" data-mp="33go8339aiiwa">{content}</div>
      </div>
    </div>;
}